const API_BASE_URL = 'https://4vkpvv0re3.execute-api.ap-southeast-2.amazonaws.com/v1';

class TestDataAPI {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/test-data`;
  }

  // 获取认证头
  getAuthHeaders(idToken) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    };
  }  // 创建新的TestData
  async createTestData(studentName, testResponse, testTemplateId, idToken, options = {}) {
    try {
      const createData = {
        studentName,
        testResponse: JSON.stringify(testResponse),
        testTemplateId
      };

      // Portal integration optional fields
      if (options.studentId) createData.studentId = options.studentId;
      if (options.courseGroupId) createData.courseGroupId = options.courseGroupId;
      if (options.campusId) createData.campusId = options.campusId;
      // 考试批次归属（后端未传时会回落到模板的currentBatchId）
      if (options.batchId) createData.batchId = options.batchId;
      
      // If testResponse has reportTestResponse field (for mock tests), include it
      if (testResponse.reportTestResponse) {
        createData.reportTestResponse = JSON.stringify(testResponse.reportTestResponse);
        // Remove reportTestResponse from the main testResponse before saving
        const { reportTestResponse, ...mainTestResponse } = testResponse;
        createData.testResponse = JSON.stringify(mainTestResponse);
      }
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'create',
          data: createData
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        ...result,
        testResponse: typeof result.testResponse === 'string' 
          ? JSON.parse(result.testResponse) 
          : result.testResponse
      };
    } catch (error) {
      console.error('Error creating test data:', error);
      throw error;
    }
  }

  // 获取单个TestData
  async getTestData(testDataId, idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'read',
          data: {
            id: testDataId
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        ...result,
        testResponse: typeof result.testResponse === 'string' 
          ? JSON.parse(result.testResponse) 
          : result.testResponse
      };
    } catch (error) {
      console.error('Error fetching test data:', error);
      throw error;
    }
  }

  // 获取所有TestData
  async getAllTestData(idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'read',
          data: {}
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.map(testData => ({
        ...testData,
        testResponse: typeof testData.testResponse === 'string' 
          ? JSON.parse(testData.testResponse) 
          : testData.testResponse
      }));
    } catch (error) {
      console.error('Error fetching test data:', error);
      throw error;
    }
  }

  // 获取指定模板的TestData (使用GSI)，可选按考试批次过滤
  async getTestDataByTemplateId(testTemplateId, idToken, includeArchived = false, summary = false, batchId = null) {
    try {
      const requestBody = {
        operation: 'read',
        data: {
          testTemplateId: testTemplateId,
          includeArchived: includeArchived,
          summary: summary
        }
      };
      if (batchId) {
        requestBody.data.batchId = batchId;
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API error response:`, errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const result = await response.json();

      const processedResult = result.map(testData => ({
        ...testData,
        ...(testData.testResponse ? {
          testResponse: typeof testData.testResponse === 'string'
            ? JSON.parse(testData.testResponse)
            : testData.testResponse
        } : {})
      }));

      return processedResult;
    } catch (error) {
      console.error('Error fetching test data by template ID:', error);
      throw error;
    }
  }
  // 更新TestData
  async updateTestData(testDataId, studentName, testResponse, idToken, options = {}) {
    try {
      const updateData = {
        id: testDataId,
        studentName,
        testResponse: JSON.stringify(testResponse)
      };

      // 考试批次归属（可在编辑时改派批次）
      if (options.batchId) updateData.batchId = options.batchId;
      
      // If testResponse has reportTestResponse field (for mock tests), include it
      if (testResponse.reportTestResponse) {
        updateData.reportTestResponse = JSON.stringify(testResponse.reportTestResponse);
        // Remove reportTestResponse from the main testResponse before saving
        const { reportTestResponse, ...mainTestResponse } = testResponse;
        updateData.testResponse = JSON.stringify(mainTestResponse);
      }
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'update',
          data: updateData
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating test data:', error);
      throw error;
    }
  }

  // 删除TestData
  async deleteTestData(testDataId, idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'delete',
          data: {
            id: testDataId
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting test data:', error);
      throw error;
    }
  }

  // 批量更新响应中的section名称
  async updateSectionNames(templateId, oldSectionName, newSectionName, idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'update_section_names',
          data: {
            templateId,
            oldSectionName,
            newSectionName
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating section names:', error);
      throw error;
    }
  }

  // 批量归档/取消归档模板下所有响应
  async archiveByTemplate(testTemplateId, isArchived, idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'archive',
          data: {
            testTemplateId,
            isArchived
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error archiving test data:', error);
      throw error;
    }
  }

  // 计算分数 - Legacy method for Final Exam scores
  async calculateScores(templateId, mockParameters, idToken) {
    try {
      const requestData = {
        templateId
      };
      
      // 如果提供了mockParameters，添加到请求数据中
      if (mockParameters) {
        requestData.mockParameters = mockParameters;
      }
      
      // Use test-report endpoint for calculate-scores operation
      const reportBaseUrl = `${API_BASE_URL}/test-report`;
      const response = await fetch(reportBaseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'calculate-scores',
          data: requestData
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error calculating scores:', error);
      throw error;
    }
  }

  // 计算Mock Test原始分数
  async calculateMockTestScores(templateId, idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'calculate-mock-test-scores',
          data: {
            templateId
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error calculating mock test scores:', error);
      throw error;
    }
  }

  // 计算Mock Test转换分数
  async calculateTransformedMockTestScores(templateId, transformParameters, idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'calculate-transformed-mock-test-scores',
          data: {
            templateId,
            transformParameters
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error calculating transformed mock test scores:', error);
      throw error;
    }
  }

  // 计算Mock Test传统线性转换分数
  async calculateTraditionalTransformedMockTestScores(templateId, traditionalParameters, idToken) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(idToken),
        body: JSON.stringify({
          operation: 'calculate-traditional-transformed-mock-test-scores',
          data: {
            templateId,
            traditionalParameters
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error calculating traditional transformed mock test scores:', error);
      throw error;
    }
  }
}

export const testDataAPI = new TestDataAPI();
